package app.alefly.analytics

import app.alefly.domain.model.ChallengeId
import app.alefly.domain.model.Difficulty
import app.alefly.domain.model.Platform
import app.alefly.domain.model.QuizId
import app.alefly.domain.model.TenantId

interface SocialEventFactory {
    fun rankingSubmit(
        tenantId: TenantId,
        quizId: QuizId,
        challengeId: ChallengeId,
        difficulty: Difficulty,
        locale: String,
        platform: Platform,
        status: String,
        score: Int? = null,
        total: Int? = null,
        durationMs: Long? = null,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.RANKING_SUBMIT,
        context = AnalyticsContext(
            tenantId = tenantId,
            quizId = quizId,
            challengeId = challengeId,
            difficulty = difficulty,
            locale = locale,
            platform = platform,
        ),
        attributes = buildMap {
            put("status", status)
            score?.let { put("score", it.toString()) }
            total?.let { put("total", it.toString()) }
            durationMs?.let { put("durationMs", it.toString()) }
        },
    )

    fun rankingView(
        tenantId: TenantId,
        challengeId: ChallengeId,
        difficulty: Difficulty,
        locale: String,
        platform: Platform,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.RANKING_VIEW,
        context = AnalyticsContext(
            tenantId = tenantId,
            challengeId = challengeId,
            difficulty = difficulty,
            locale = locale,
            platform = platform,
        ),
    )

    fun shareOpen(
        tenantId: TenantId,
        challengeId: ChallengeId,
        locale: String,
        platform: Platform,
        difficulty: Difficulty,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.SHARE_OPEN,
        context = AnalyticsContext(
            tenantId = tenantId,
            challengeId = challengeId,
            locale = locale,
            platform = platform,
            difficulty = difficulty,
        ),
    )

    fun shareSuccess(
        tenantId: TenantId,
        challengeId: ChallengeId,
        locale: String,
        platform: Platform,
        channel: String,
        difficulty: Difficulty,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.SHARE_SUCCESS,
        context = AnalyticsContext(
            tenantId = tenantId,
            challengeId = challengeId,
            locale = locale,
            platform = platform,
            difficulty = difficulty,
        ),
        attributes = mapOf(
            "channel" to channel,
        ),
    )

    fun inviteLanding(
        tenantId: TenantId,
        challengeId: ChallengeId,
        locale: String,
        platform: Platform,
        difficulty: Difficulty,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.INVITE_LANDING,
        context = AnalyticsContext(
            tenantId = tenantId,
            challengeId = challengeId,
            locale = locale,
            platform = platform,
            difficulty = difficulty,
        ),
    )

    fun inviteAccepted(
        tenantId: TenantId,
        quizId: QuizId,
        challengeId: ChallengeId,
        locale: String,
        platform: Platform,
        difficulty: Difficulty,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.INVITE_ACCEPTED,
        context = AnalyticsContext(
            tenantId = tenantId,
            quizId = quizId,
            challengeId = challengeId,
            locale = locale,
            platform = platform,
            difficulty = difficulty,
        ),
    )

    fun installCtaClick(
        tenantId: TenantId,
        locale: String,
        platform: Platform,
        difficulty: Difficulty,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.INSTALL_CTA_CLICK,
        context = AnalyticsContext(
            tenantId = tenantId,
            locale = locale,
            platform = platform,
            difficulty = difficulty,
        ),
    )

    fun globalChallengeAction(
        action: GlobalChallengeAction,
        tenantId: TenantId,
        challengeId: ChallengeId,
        locale: String,
        platform: Platform,
        notificationEvent: String? = null,
        participantStatus: String? = null,
        statusSource: String? = null,
        decision: String? = null,
        decisionReason: String? = null,
        entrySource: String? = null,
        cardVariant: String? = null,
    ): AnalyticsEvent = AnalyticsEvent(
        name = AnalyticsEventName.GLOBAL_CHALLENGE_ACTION,
        context = AnalyticsContext(tenantId = tenantId, challengeId = challengeId, locale = locale, platform = platform),
        attributes = buildMap {
            put("action", action.wireValue)
            notificationEvent?.let { put("notification_event", it) }
            participantStatus?.let { put("participant_status", it) }
            statusSource?.let { put("status_source", it) }
            decision?.let { put("decision", it) }
            decisionReason?.let { put("decision_reason", it) }
            entrySource?.let { put("entry_source", it) }
            cardVariant?.let { put("card_variant", it) }
        },
    )
}
